import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { AvatarModule } from 'primeng/avatar';
import { SelectModule } from 'primeng/select';
import { PasswordModule } from 'primeng/password';
import { FloatLabelModule } from 'primeng/floatlabel';
import { Subscription } from 'rxjs';
import { User } from '../../models/user.model';
import { DataService } from '../../services/data';
import { FirestoreService } from '../../services/firestore';
import { MemberService } from '../../services/member.service';
import { resolveLoggedInDisplayName, splitFullName } from '../../helpers/user-display-name.helper';
import { ToastService } from '../../services/toast.service';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { AuthService } from '../../auth.service';
import { SpinnerService } from '../../services/spinner.service';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toJSDate } from '../../helpers/fechas-helper';
import { NgxImageCompressService } from 'ngx-image-compress';
import { ImageService } from '../../services/image.service';
import { getDownloadURL, getStorage, ref, uploadString } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { Firestore } from '@angular/fire/firestore';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    DatePickerModule,
    AvatarModule,
    SelectModule,
    PasswordModule,
    FloatLabelModule,
    ReactiveFormsModule,
    ToastModule,
    TooltipModule,
    TranslateModule
  ],
  templateUrl: './profile.html',
  styleUrl: './profile.css',
  encapsulation: ViewEncapsulation.None,
})
export class Profile implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  form: FormGroup;
  passwordForm!: FormGroup;
  activeSection: 'info' | 'password' = 'info';
  user: User | null = null;
  showMobileMenu: boolean = false;
  locations: { name: string; code: string; }[] = [];
  imageProfile: string = 'https://primefaces.org/cdn/primeng/images/avatar/amyelsner.png';
  selectedBirthDate: Date | null = null;
  churchId: string | null = null;

  generoOptions: { label: string; value: string }[] = [];

  uidUser: string | null = null;
  memberId: string | null = null;

  previewFotoBase64: string | null = null;   // para ver en el avatar
  fotoPendienteBase64: string | null = null; // para subir después
  updatePhoto: boolean = false;
  photoUser: string | null = null;

  availableRoles = [
    { labelKey: 'USERS.ROLE_TREASURER', value: 'tesoreria' },
    { labelKey: 'USERS.ROLE_SECRETARY', value: 'secretaria' }
  ];

  availablePermissions = [
    { labelKey: 'USERS.PERM_VIEW_MEMBERSHIP', value: 'access_membresia' },
    { labelKey: 'USERS.PERM_VIEW_FINANCE', value: 'access_finanzas' },
    { labelKey: 'USERS.PERM_VIEW_LEADERS', value: 'access_usuarios' }
  ];

  constructor(
    private dataService: DataService,
    private fb: FormBuilder,
    private firestoreService: FirestoreService,
    private toastService: ToastService,
    private authService: AuthService,
    private spinnerService: SpinnerService,
    private imageCompress: NgxImageCompressService,
    private imageService: ImageService,
    private firestore: Firestore,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private memberService: MemberService
  ) {
    this.form = this.fb.group({
      nombres: ['', Validators.required],
      apellidos: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      direccion: [''],
      telefono: [''],
      fechaNacimiento: [''],
      genero: [''],
      ciudad: [''],
      codigoPostal: [''],
      photoURL: [''],
      roles: ['']
    });

    this.passwordForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required],
    });

  }

  ngOnInit() {
    this.buildGeneroOptions();
    this.subscriptions.add(
      this.translate.onLangChange.subscribe(() => {
        this.buildGeneroOptions();
        this.cdr.detectChanges();
      })
    );
    this.suscripciones();
  }

  private buildGeneroOptions(): void {
    this.generoOptions = [
      { label: this.translate.instant('MEMBER_DETAIL.OPT_GENDER_MALE'), value: 'masculino' },
      { label: this.translate.instant('MEMBER_DETAIL.OPT_GENDER_FEMALE'), value: 'femenino' }
    ];
  }

  suscripciones() {
    this.subscriptions.add(
      this.dataService.dataCurrentUser$.subscribe(userData => {
        const user = Array.isArray(userData) ? userData[0] : userData;
        if (!user) {
          return;
        }
        void this.hydrateProfileForm(user);
      })
    );

    this.subscriptions.add(
      this.dataService.churchId$.subscribe(churchId => {
        this.churchId = churchId;
        if (this.user && churchId) {
          void this.hydrateProfileForm(this.user);
        }
      })
    );

    this.subscriptions.add(
      this.dataService.uidUser$.subscribe(uid => {
        this.uidUser = uid;
      })
    );
  }

  private hydratingMemberId: string | null = null;

  private async hydrateProfileForm(user: User): Promise<void> {
    const toDateIfTs = (v: any) => (v && typeof v?.toDate === 'function') ? v.toDate() : v;
    let nombres = (user.nombres || '').trim();
    let apellidos = (user.apellidos || '').trim();
    if (!nombres && !apellidos && user.nombreCompleto) {
      const split = splitFullName(user.nombreCompleto);
      nombres = split.nombres;
      apellidos = split.apellidos;
    }

    const isThin = !nombres && !apellidos;
    this.user = user;
    this.photoUser = user.photoUrl || null;
    this.memberId = user.idDocMember || null;

    if (isThin && this.churchId && this.memberId && this.hydratingMemberId !== this.memberId) {
      this.hydratingMemberId = this.memberId;
      try {
        const member = await this.memberService.getMemberById(this.churchId, this.memberId);
        if (member) {
          nombres = (member.nombres || '').trim();
          apellidos = (member.apellidos || '').trim();
          if (!nombres && !apellidos) {
            const split = splitFullName(resolveLoggedInDisplayName({ user: member as any }));
            nombres = split.nombres;
            apellidos = split.apellidos;
          }
          this.photoUser = this.photoUser || member.photoUrl || null;
        }
      } catch {
        // El formulario queda con lo que haya en usuarios
      }
    }

    this.form.patchValue({
      nombres,
      apellidos,
      email: user.email || '',
      direccion: user.direccion || '',
      telefono: user.telefono || '',
      fechaNacimiento: toDateIfTs(user.fechaNacimiento) || '',
      genero: user.genero || '',
      ciudad: user.ciudad || '',
      codigoPostal: user.codigoPostal || '',
      photoUrl: user.photoUrl || this.photoUser || '',
      roles: user.roles || ''
    });
    this.cdr.detectChanges();
  }

  get nombres() { return this.form.get('nombres')?.value || ''; }
  get apellidos() { return this.form.get('apellidos')?.value || ''; }
  get photoURL() { return this.form.get('photoURL')?.value || ''; }


  async onFotoSeleccionada(event: any) {
    const file: File = event.target.files?.[0];
    if (!file) return;

    const formatosPermitidos = ['image/jpeg', 'image/png', 'image/webp'];
    if (!formatosPermitidos.includes(file.type)) {
      this.toastService.showWarning(this.translate.instant('USERS.PROFILE.TOAST_WARN_IMAGE_FORMAT'));
      return;
    }

    this.spinnerService.show();
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const originalBase64 = reader.result as string;

          // Comprimir y redimensionar
          const compressed = await this.imageCompress.compressFile(originalBase64, -1, 75, 75);
          const resizedBase64 = await this.imageService.resizeImageBase64(compressed, 250, 250);

          // 👉 Solo previsualizar y marcar como “pendiente de subir”
          this.previewFotoBase64 = resizedBase64;
          this.fotoPendienteBase64 = resizedBase64;

          // Refresca el avatar
          this.form.get('photoUrl')?.setValue(resizedBase64);
          this.updatePhoto = true;
          this.toastService.showInfo(this.translate.instant('USERS.PROFILE.TOAST_INFO_PHOTO_READY'));
        } catch (e) {
          this.toastService.showError(this.translate.instant('USERS.PROFILE.TOAST_ERR_IMAGE'));
        } finally {
          this.spinnerService.hide();
        }
      };
      reader.readAsDataURL(file);
    } catch {
      this.spinnerService.hide();
    }
  }

  async saveChanges(): Promise<void> {
    if (this.form.valid) {
      if (!this.churchId) {
        this.toastService.showError(this.translate.instant('USERS.PROFILE.TOAST_ERR_NO_CHURCH_SAVE'));
        return;
      }
      this.spinnerService.show();
      try {
        const functions = getFunctions();
        const updateCurrentUserProfileCallable = httpsCallable(functions, 'updateCurrentUserProfile');
        if (!this.user || !this.uidUser) {
          this.toastService.showError(this.translate.instant('USERS.PROFILE.TOAST_ERR_NO_UID'));
          this.spinnerService.hide();
          return;
        }

        let fechaDate: Date | null = null;
        try {
          const rawDate = this.form.value.fechaNacimiento;
          if (rawDate) {
            fechaDate = toJSDate(rawDate);
            if (isNaN(fechaDate.getTime())) {
              fechaDate = null;
            }
          }
        } catch (e) {
          fechaDate = null;
        }

        let sanitizedFormValue = {
          ...this.form.value,
          fechaNacimiento: fechaDate
        };

        const payload = {
          churchId: this.churchId,
          id: this.uidUser,
          datos: { ...sanitizedFormValue }
        };
        await updateCurrentUserProfileCallable(payload);

        const displayName = resolveLoggedInDisplayName({
          user: {
            nombres: sanitizedFormValue.nombres,
            apellidos: sanitizedFormValue.apellidos,
          },
        });
        if (displayName) {
          this.dataService.setCurrentDisplayName(displayName);
          try {
            const updateAuthProfile = httpsCallable(functions, 'updateUserProfile');
            await updateAuthProfile({ uid: this.uidUser, displayName });
          } catch {
            // El perfil local ya se guardó; Auth se puede backfillear en el siguiente login
          }
        }

        // 3. SUBIR FOTO SI APLICA
        if (this.updatePhoto) {
          await this.subirFotoSiPendiente(this.churchId, this.uidUser);
          this.updatePhoto = false;
        }

        this.toastService.showSuccess(this.translate.instant('USERS.PROFILE.TOAST_OK_PROFILE'));
      } catch (error: any) {
        this.toastService.showError(error?.message || this.translate.instant('USERS.PROFILE.TOAST_ERR_PROFILE'));
      } finally {
        this.spinnerService.hide();
      }
    } else {
      this.toastService.showError(this.translate.instant('USERS.PROFILE.TOAST_ERR_REQUIRED_FIELDS'));
    }
  }

  private async subirFotoSiPendiente(churchId: string, uidUser: string) {
    if (!this.fotoPendienteBase64) return null;

    const storage = getStorage();
    const rutaRef = ref(storage, `iglesias/${churchId}/perfiles/${uidUser}.jpg`);

    await uploadString(rutaRef, this.fotoPendienteBase64, 'data_url');
    const url = await getDownloadURL(rutaRef);

    // Actualiza miembro
    const miembroRef = doc(this.firestore, `iglesias/${churchId}/miembros/${this.memberId}`);
    await setDoc(miembroRef, { photoUrl: url }, { merge: true });

    // Si existe uidUser válido, actualiza usuarios y (opcional) claims
    const esUidValido = typeof this.uidUser === 'string' && this.uidUser.trim().length === 28;
    if (esUidValido) {
      const usuarioRef = doc(this.firestore, `iglesias/${churchId}/usuarios/${this.uidUser}`);
      await setDoc(usuarioRef, { photoUrl: url }, { merge: true });

      try {
        const functions = getFunctions();
        const updateUserClaims = httpsCallable(functions, 'updateUserProfile');
        await updateUserClaims({ uid: this.uidUser, photoUrl: url });
      } catch {
        // no bloquees el flujo si falla la función
      }
    }

    // Limpia el pendiente y deja el preview apuntando al URL final
    this.fotoPendienteBase64 = null;
    this.previewFotoBase64 = url;
    this.form.get('photoUrl')?.setValue(url);
    this.photoUser = url;
    this.dataService.setCurrentPhotoUser(url);
    return url;
  }

  discardChanges() {
    if (this.user) {
      this.form.patchValue({
        nombres: this.user.nombres || '',
        apellidos: this.user.apellidos || '',
        email: this.user.email || '',
        direccion: this.user.direccion || '',
        telefono: this.user.telefono || '',
        fechaNacimiento: this.user.fechaNacimiento || '',
        genero: this.user.genero || '',
        ciudad: this.user.ciudad || '',
        codigoPostal: this.user.codigoPostal || '',
        photoUrl: this.user.photoUrl || ''
      });
    }
    this.toastService.showInfo(this.translate.instant('USERS.PROFILE.TOAST_INFO_DISCARD'));
  }

  setSection(section: 'info' | 'password'): void {
    this.activeSection = section;
    this.showMobileMenu = false;
  }

  updatePassword() {
    this.setSection('password');
  }

  getInitials(): string {
    const nombres: string = this.form.get('nombres')?.value?.trim() || '';
    const apellidos: string = this.form.get('apellidos')?.value?.trim() || '';
    if (nombres && apellidos) {
      // Primera letra del primer nombre y primera letra del primer apellido
      return nombres.charAt(0).toUpperCase() + apellidos.charAt(0).toUpperCase();
    } else if (nombres) {
      const partes = nombres.split(' ');
      if (partes.length > 1) {
        // Primera letra del primer nombre y primera letra del segundo nombre
        return partes[0].charAt(0).toUpperCase() + partes[1].charAt(0).toUpperCase();
      } else {
        // Solo un nombre, devuelve la primera letra dos veces
        return nombres.charAt(0).toUpperCase() + nombres.charAt(0).toUpperCase();
      }
    }
    return '';
  }

  cancelPasswordChange(): void {
    this.passwordForm.reset();
  }

  async changePassword(): Promise<void> {
    const { newPassword, confirmPassword } = this.passwordForm.value;
    if (!this.passwordForm.valid) {
      this.toastService.showError(this.translate.instant('USERS.PROFILE.TOAST_ERR_PASSWORD_FIELDS'));
      return;
    }
    if ((newPassword?.length ?? 0) < 6) {
      this.toastService.showWarning(this.translate.instant('USERS.TOAST_WARN_PASSWORD_MIN'));
      return;
    }
    if (newPassword !== confirmPassword) {
      this.toastService.showError(this.translate.instant('USERS.PROFILE.TOAST_ERR_PASSWORD_MISMATCH'));
      this.cancelPasswordChange();
      return;
    }
    try {
      this.spinnerService.show();
      await this.firestoreService.changeUserPassword(newPassword);
      this.spinnerService.hide();
      this.toastService.showSuccess(this.translate.instant('USERS.PROFILE.TOAST_OK_PASSWORD'));
      this.cancelPasswordChange();
    } catch (error: any) {
      if (error?.code === 'auth/requires-recent-login') {
        this.spinnerService.hide();
        this.cancelPasswordChange();
        this.toastService.showWarning(this.translate.instant('USERS.PROFILE.TOAST_WARN_PASSWORD_RECENT_LOGIN'));
      } else {
        this.spinnerService.hide();
        this.cancelPasswordChange();
        this.toastService.showError(this.translate.instant('USERS.PROFILE.TOAST_ERR_PASSWORD_CHANGE'));
      }
    }
  }

  handleImageError() {
    this.form.get('photoUrl')?.setValue(null);
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }

  private normalizeRoles(roles: any): string[] {
    if (!roles) return [];
    if (typeof roles === 'string') return [roles];
    if (Array.isArray(roles)) {
      return roles.map(r => {
        if (typeof r === 'string') return r;
        if (typeof r === 'object' && r && (r as any).value) return (r as any).value;
        return null;
      }).filter((r): r is string => r !== null && r !== '');
    }
    return [];
  }

  getMainRoleKind(user: any): 'PASTOR' | 'ADMIN' | 'TESORERIA' | 'SECRETARIA' | 'NONE' {
    if (!user) return 'NONE';
    const roles = this.normalizeRoles(user.roles);
    if (roles.some(r => r.toLowerCase() === 'pastor')) return 'PASTOR';
    if (roles.some(r => r.toLowerCase() === 'admin')) return 'ADMIN';
    const mainVals = this.availableRoles.map(r => r.value.toLowerCase());
    const found = roles.find(val => mainVals.includes(val.toLowerCase()));
    if (found) {
      const lv = found.toLowerCase();
      if (lv === 'tesoreria') return 'TESORERIA';
      if (lv === 'secretaria') return 'SECRETARIA';
    }
    return 'NONE';
  }

  getMainRoleLabel(user: any): string {
    const k = this.getMainRoleKind(user);
    const keys: Record<string, string> = {
      PASTOR: 'USERS.MAIN_ROLE_PASTOR',
      ADMIN: 'USERS.MAIN_ROLE_ADMIN',
      TESORERIA: 'USERS.ROLE_TREASURER',
      SECRETARIA: 'USERS.ROLE_SECRETARY',
      NONE: 'USERS.MAIN_ROLE_NONE'
    };
    return this.translate.instant(keys[k] || 'USERS.MAIN_ROLE_NONE');
  }

  hasNoMainRole(user: any): boolean {
    return this.getMainRoleKind(user) === 'NONE';
  }

  private accessShortLabel(val: string): string {
    const map: Record<string, string> = {
      access_membresia: 'USERS.SHORT_MEMBERSHIP',
      access_finanzas: 'USERS.SHORT_FINANCE',
      access_usuarios: 'USERS.SHORT_LEADERS',
      access_reports: 'USERS.SHORT_REPORTS'
    };
    const key = map[val];
    return key ? this.translate.instant(key) : this.formatAccessName(val);
  }

  getAccessTags(user: any): string[] {
    if (!user) return [];
    if (this.getMainRoleKind(user) === 'PASTOR') {
      return [this.translate.instant('USERS.ACCESS_TOTAL')];
    }
    if (!user.roles || !Array.isArray(user.roles)) return [];

    const accessList: string[] = [];
    user.roles.forEach((r: any) => {
      const val = typeof r === 'string' ? r : r.value;
      if (typeof val === 'string' && val.startsWith('access_')) {
        accessList.push(this.accessShortLabel(val));
      }
    });

    return accessList;
  }

  formatAccessName(val: string): string {
    return val.replace('access_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
